import { useEffect, useMemo, useState } from "react";
import { flaschenVonWein } from "../kern/fachlogik.js";
import { planZahlen, wetterPlan } from "../dienste/wetterlauf.js";
import { Auswahl, Btn, Feld, Leer, LoeschenKnopf } from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";


/** Bestätigungsdialog „Keller löschen" — leert den Bestand und,
    optional, auch die Stammdaten zu Weinen, Winzern und Regionen. */
/** Zusammenfassung nach einem Excel-Import. */
function WetterLadenPanel({ db, status, onStart, onClose }) {
  /* Was „Nur fehlende“ und „Alle“ tun würden — derselbe Plan, den der
     Lauf abarbeitet (dienste/wetterlauf.js). */
  const info = useMemo(() => {
    const plan = wetterPlan(db, "fehlend");
    return { plan, fehlend: planZahlen(plan), alle: planZahlen(wetterPlan(db, "alle")) };
  }, [db]);
  /* Sekundentakt nur, solange an der Minutengrenze gewartet wird. */
  const [, tick] = useState(0);
  useEffect(() => {
    if (!status || !status.wartetBis) return undefined;
    const iv = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, [status && status.wartetBis]);

  const umfang = (z) => [
    z.reihen > 0 && t("{n} Klimareihen vom Server", { n: z.reihen }),
    z.server > 0 && t("bis zu {n} Jahrgänge vom Server", { n: z.server }),
    z.ableiten > 0 && t("{n} Jahrgänge aus der Klimareihe, ohne Abruf", { n: z.ableiten }),
  ].filter(Boolean).join(" · ");

  if (status && status.phase === "laeuft") {
    const proz = status.gesamt ? Math.round((status.erledigt / status.gesamt) * 100) : 0;
    const rest = status.wartetBis ? Math.max(0, Math.ceil((status.wartetBis - Date.now()) / 1000)) : 0;
    return (
      <>
        <p className="wl-fliesstext">{t("Die Wetterdaten werden geladen. Bitte das Fenster geöffnet lassen. Was schon geholt ist, liegt auch auf dem Server bereit.")}</p>
        <div className="wl-wetterbalken"><div className="wl-wetterbalken-fill" style={{ width: `${proz}%` }} /></div>
        <p className="wl-wetterfortschritt num">{status.erledigt} / {status.gesamt}</p>
        {status.wartetBis && (
          <p className="wl-wetterwarten" role="status">{t("Minutengrenze bei Open-Meteo erreicht — es geht in {n} s weiter.", { n: rest })}</p>
        )}
      </>
    );
  }

  if (status && (status.phase === "fertig" || status.phase === "abgebrochen")) {
    const zeile = (label, wert) => (wert > 0 ? <div className="wl-datenpaar"><dt>{t(label)}</dt><dd className="num">{wert}</dd></div> : null);
    return (
      <>
        <p className="wl-fliesstext">{status.phase === "fertig"
          ? t(status.modus === "fehlend" ? "Fertig. Fehlende Daten wurden aufgefüllt." : "Fertig. Alle Daten wurden aktualisiert.")
          : t("Abgebrochen — das Kontingent bei Open-Meteo ist für den Moment erschöpft.")}</p>
        {status.abbruch && (
          <div className="wl-wetterabbruch" role="alert">
            <p>{status.abbruch.text}</p>
            <p>{t("Noch offen: {n}. Was schon geholt ist, bleibt erhalten — später erneut „Nur fehlende auffüllen“ wählen.", { n: status.abbruch.offen })}</p>
          </div>
        )}
        <dl className="wl-datenliste">
          {zeile("Klimareihen geladen", status.reihen)}
          {zeile("Jahrgänge vom Server", status.server)}
          {zeile("Jahrgänge aus der Klimareihe", status.abgeleitet)}
          {zeile("Ohne Vegetationsdaten", status.ohneDaten)}
          {zeile("Fehler", status.fehler)}
          {status.reihen + status.server + status.abgeleitet + status.ohneDaten + status.fehler === 0 && (
            <div className="wl-datenpaar"><dt>{t("Nichts zu tun")}</dt><dd className="num">0</dd></div>
          )}
        </dl>
        {status.ohneDaten > 0 && <p className="wl-statleer">{t("Ohne Vegetationsdaten sind Jahrgänge, deren Saison noch nicht begonnen hat — das ist normal.")}</p>}
        {status.fehler > 0 && status.letzterFehler && <p className="wl-fehler">{t("Letzter Fehler: {text}", { text: status.letzterFehler })}</p>}
        <div className="wl-modalfuss"><div className="wl-modalfuss-rechts"><Btn variante="voll" onClick={onClose}>{t("Schließen")}</Btn></div></div>
      </>
    );
  }

  /* Frage: nur fehlende auffüllen oder alle aktualisieren */
  const neuesJahr = info.plan.reihen.filter((r) => r.grund === "neuesJahr" || r.grund === "nachlauf").length;
  return (
    <>
      <p className="wl-fliesstext">
        {t("Klimareihen und Witterung für {r} Regionen und {j} Jahrgänge im Bestand.", { r: info.plan.regionen, j: info.plan.jahrgaengeImBestand })}
        {info.plan.ohneKoord > 0 && ` ${t("{n} Regionen ohne Koordinaten werden übersprungen.", { n: info.plan.ohneKoord })}`}
      </p>
      {neuesJahr > 0 && (
        <p className="wl-hinweis">{t("{n} Klimareihen fehlt der jüngste abgeschlossene Jahrgang oder sein vollständiger Nachlauf.", { n: neuesJahr })}</p>
      )}
      <div className="wl-wetterwahl">
        <button className="wl-wetteropt" onClick={() => onStart("fehlend")}>
          <span className="wl-wetteropt-titel">{t("Nur fehlende auffüllen")}</span>
          <span className="wl-wetteropt-text">{info.fehlend.gesamt === 0 ? t("Alles bereits vorhanden — nichts zu laden.") : umfang(info.fehlend)}</span>
        </button>
        <button className="wl-wetteropt" onClick={() => onStart("alle")}>
          <span className="wl-wetteropt-titel">{t("Alle aktualisieren")}</span>
          <span className="wl-wetteropt-text">{umfang(info.alle)}</span>
        </button>
      </div>
      <p className="wl-statleer">{t("Der Server hält Klimareihen vorrätig; Open-Meteo wird nur für Fehlendes gefragt. Eine Region zum ersten Mal kostet dort viel Kontingent — wird es knapp, hält der Lauf an und sagt es.")}</p>
      <div className="wl-modalfuss"><div className="wl-modalfuss-rechts"><Btn onClick={onClose}>{t("Abbrechen")}</Btn></div></div>
    </>
  );
}


function ImportBericht({ bericht, onClose }) {
  const zeilen = [
    ["Regionen", bericht.regionenNeu, bericht.regionenVerknuepft],
    ["Winzer", bericht.winzerNeu, bericht.winzerVerknuepft],
    ["Weine", bericht.weineNeu, bericht.weineAktualisiert],
    ["Jahrgänge", bericht.jahrgaengeNeu, bericht.jahrgaengeAktualisiert],
    ["Kritiken", bericht.kritikenNeu, null],
    ["Getrunken", bericht.getrunkenNeu, null],
    ["Zugänge (Kaufhistorie)", bericht.zugaengeNeu != null ? bericht.zugaengeNeu : 0, null],
    ["Weinkarten-Positionen", bericht.weinkartenNeu != null ? bericht.weinkartenNeu : 0, null],
  ];
  return (
    <>
      <p className="wl-fliesstext">{t("Die Excel-Datei wurde eingelesen und mit deinem Keller zusammengeführt.")}</p>
      <div className="wl-scroll">
        <table className="wl-tabelle wl-tabelle--kompakt">
          <thead><tr><th>{t("Bereich")}</th><th>{t("Neu angelegt")}</th><th>{t("Aktualisiert / verknüpft")}</th></tr></thead>
          <tbody>
            {zeilen.map(([bez, neu, upd]) => (
              <tr key={bez}>
                <td>{bez}</td>
                <td className="num">{neu}</td>
                <td className="num">{upd == null ? "—" : upd}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {bericht.warnungen && bericht.warnungen.length > 0 && (
        <div className="wl-importwarnung">
          <span className="wl-feldname">{t("Hinweise")}</span>
          <ul>{bericht.warnungen.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}
      <div className="wl-modalfuss">
        <div className="wl-modalfuss-rechts">
          <Btn variante="voll" onClick={onClose}>{t("Schließen")}</Btn>
        </div>
      </div>
    </>
  );
}


function KellerLoeschenForm({ db, onLoeschen, onCancel }) {
  const [auchStammdaten, setAuchStammdaten] = useState(false);
  const [scharf, setScharf] = useState(false);

  const flaschen = db.weine.reduce((s, w) => s + flaschenVonWein(w), 0);
  const anzahlWeine = db.weine.length;
  const anzahlWinzer = db.winzer.length;
  const anzahlRegionen = db.regions.length;

  return (
    <>
      <p className="wl-fliesstext">
        {auchStammdaten
          ? <>Es werden <strong>alle Weine, Winzer und Regionen</strong> samt Beständen, Jahrgängen, Kritiken und Historie unwiderruflich gelöscht. Übrig bleibt ein leerer Keller.</>
          : <>Es werden <strong>alle Bestände und Jahrgänge</strong> samt Kritiken und Bewegungshistorie gelöscht. Weine, Winzer und Regionen bleiben als Stammdaten erhalten.</>}
      </p>
      <dl className="wl-fakten">
        <dt>{t("Betroffen")}</dt>
        <dd className="num">{flaschen} Flaschen · {anzahlWeine} Weine{auchStammdaten ? ` · ${anzahlWinzer} Winzer · ${anzahlRegionen} Regionen` : ""}</dd>
      </dl>
      <label className="wl-check wl-loeschopt">
        <input type="checkbox" checked={auchStammdaten} onChange={(e) => { setAuchStammdaten(e.target.checked); setScharf(false); }} />
        Auch Weine, Winzer und Regionen löschen
      </label>
      <p className="wl-hinweis">{t("Dieser Schritt kann nicht rückgängig gemacht werden.")}</p>
      <div className="wl-modalfuss">
        <div className="wl-modalfuss-rechts">
          <Btn variante="ghost" onClick={onCancel}>{t("Abbrechen")}</Btn>
          <Btn variante={scharf ? "gefahrScharf" : "gefahr"}
            onClick={() => (scharf ? onLoeschen(auchStammdaten) : setScharf(true))}>
            {scharf ? "Wirklich unwiderruflich löschen?" : (auchStammdaten ? "Keller & Stammdaten löschen" : "Keller löschen")}
          </Btn>
        </div>
      </div>
    </>
  );
}

/**
 * Verwaltung für Administratoren: Registrierung öffnen oder sperren
 * und Konten einsehen bzw. löschen. Alle Rechte werden serverseitig
 * geprüft — dieses Panel ist nur die Oberfläche dazu.
 */
function AdminPanel({ onClose }) {
  const [daten, setDaten] = useState(null);   /* null = lädt */
  const [fehler, setFehler] = useState(null);
  const [busy, setBusy] = useState(false);

  const laden = async () => {
    setFehler(null);
    try {
      const r = await fetch("/api/admin/uebersicht");
      const j = await r.json();
      if (!r.ok) throw new Error(j.fehler || "Übersicht nicht verfügbar.");
      setDaten(j);
    } catch (e) {
      setDaten(false);
      setFehler(e.message);
    }
  };

  useEffect(() => { laden(); }, []);

  /* Zuschaltbare Merkmale je Konto — „weinkarten" und „wetter". */
  const setzeMerkmal = async (n, merkmal, erlaubt) => {
    const feld = merkmal === "wetter" ? "darfWetter"
      : merkmal === "katalog" ? "darfKatalog" : "darfWeinkarten";
    setBusy(true); setFehler(null);
    try {
      const r = await fetch(`/api/admin/nutzer/${n.id}/merkmal`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ merkmal, erlaubt }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.fehler || "Speichern fehlgeschlagen.");
      setDaten((d) => ({
        ...d,
        nutzer: d.nutzer.map((x) => x.id === n.id ? { ...x, [feld]: erlaubt } : x),
      }));
    } catch (e) {
      setFehler(e.message);
    } finally { setBusy(false); }
  };

  const [katalogMeldung, setKatalogMeldung] = useState(null);
  const [katalog, setKatalog] = useState(null);
  const [zfTyp, setZfTyp] = useState("wein");
  const [zfQuelle, setZfQuelle] = useState("");
  const [zfZiel, setZfZiel] = useState("");

  const katalogHolen = async () => {
    try {
      const r = await fetch("/api/katalog");
      const j = await r.json();
      if (r.ok && j.daten) setKatalog(j.daten);
    } catch (e) { /* nicht kritisch */ }
  };
  useEffect(() => { katalogHolen(); }, []);

  const katalogZusammenfuehren = async () => {
    setBusy(true); setFehler(null); setKatalogMeldung(null);
    try {
      const r = await fetch("/api/admin/katalog/zusammenfuehren", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ typ: zfTyp, quelleId: zfQuelle, zielId: zfZiel }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.fehler || "Zusammenführen fehlgeschlagen.");
      setKatalogMeldung(`Im Katalog zusammengeführt — neuer Stand ${j.version}. Cloud-Konten ziehen automatisch nach.`);
      setZfQuelle(""); setZfZiel("");
      await katalogHolen();
    } catch (e) { setFehler(e.message); } finally { setBusy(false); }
  };
  const setzeRegistrierung = async (offen) => {
    setBusy(true); setFehler(null);
    try {
      const r = await fetch("/api/admin/registrierung", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offen }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.fehler || "Speichern fehlgeschlagen.");
      setDaten((d) => ({ ...d, registrierungOffen: j.registrierungOffen }));
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy(false);
    }
  };

  const loescheNutzer = async (n) => {
    setBusy(true); setFehler(null);
    try {
      const r = await fetch(`/api/admin/nutzer/${encodeURIComponent(n.id)}`, { method: "DELETE" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.fehler || "Löschen fehlgeschlagen.");
      await laden();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy(false);
    }
  };

  const datum = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "—"
      : `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  };

  if (daten === null) return <p className="wl-hinweis">{t("Übersicht wird geladen …")}</p>;
  if (!daten) {
    return (
      <>
        {fehler && <p className="wl-fehler">{fehler}</p>}
        <div className="wl-modalfuss"><div className="wl-modalfuss-rechts">
          <Btn variante="ghost" onClick={onClose}>{t("Schließen")}</Btn>
          <Btn variante="voll" onClick={laden}>{t("Erneut versuchen")}</Btn>
        </div></div>
      </>
    );
  }

  const offen = daten.registrierungOffen;
  return (
    <>
      <section className="wl-adminblock">
        <h3 className="wl-adminTitel">{t("Registrierung")}</h3>
        <p className="wl-hinweis">
          {offen
            ? "Neue Konten können angelegt werden."
            : "Die Registrierung ist gesperrt — bestehende Konten bleiben nutzbar."}
          {daten.einladungscode && " Zusätzlich ist ein Einladungscode gesetzt."}
        </p>
        <span className="wl-segment" role="group" aria-label={t("Registrierung öffnen oder sperren")}>
          <button className={"wl-segbtn" + (offen ? " ist-aktiv" : "")}
            disabled={busy} onClick={() => setzeRegistrierung(true)}>{t("Offen")}</button>
          <button className={"wl-segbtn" + (!offen ? " ist-aktiv" : "")}
            disabled={busy} onClick={() => setzeRegistrierung(false)}>{t("Gesperrt")}</button>
        </span>
      </section>

      <section className="wl-adminblock">
        <h3 className="wl-adminTitel">{t("Katalog")}</h3>
        {/* Das Sammelbefüllen aus dem eigenen Kellerbuch ist entfallen.
            Es war für die Erstaufsetzung gedacht und schrieb alles auf
            einen Schlag in den Katalog — an Vorschlägen und
            Dublettenprüfung vorbei. Der reguläre Weg führt heute über
            Menü → Katalog → Meine Vorschläge (dort auch die Freigabe
            lokal geführter Altbestände). */}
        <p className="wl-teil-info">
          Der Katalog ist der gemeinsame Stammdatensatz für Cloud-Konten. Er trägt nur
          Weine, Winzer, Regionen und Jahrgänge — niemals Bestände, Lagerorte oder
          Preise. Einträge entstehen aus den Vorschlägen der Konten; geprüft werden sie
          unter „Vorschläge prüfen“.
        </p>
        {katalogMeldung && <p className="wl-konto-ok">{katalogMeldung}</p>}

        {katalog && (
          <>
            <div className="wl-teil-trenner" />
            <span className="wl-konto-titel">{t("Im Katalog zusammenführen")}</span>
            <p className="wl-teil-info">
              Liegt etwas doppelt im Katalog, geht die Quelle im Ziel auf. Cloud-Konten
              ziehen automatisch nach — auch ihre Bestände und Verweise wandern mit.
            </p>
            <div className="wl-zf-arten" role="tablist">
              {[["wein", "Weine"], ["winzer", "Winzer"], ["region", "Regionen"]].map(([w, l]) => (
                <button key={w} role="tab" aria-selected={zfTyp === w}
                  className={zfTyp === w ? "aktiv" : ""}
                  onClick={() => { setZfTyp(w); setZfQuelle(""); setZfZiel(""); }}>{l}</button>
              ))}
            </div>
            {(() => {
              const feld = zfTyp === "wein" ? "weine" : zfTyp === "winzer" ? "winzer" : "regionen";
              const liste = [...(katalog[feld] || [])].sort((a, b) => String(a.name).localeCompare(String(b.name), "de"));
              const opt = (
                <>
                  <option value="">{t("Auswählen …")}</option>
                  {liste.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </>
              );
              return (
                <>
                  <Feld label={t("Quelle — verschwindet")} breit>
                    <Auswahl value={zfQuelle} onChange={(e) => setZfQuelle(e.target.value)}>{opt}</Auswahl>
                  </Feld>
                  <Feld label={t("Ziel — bleibt")} breit>
                    <Auswahl value={zfZiel} onChange={(e) => setZfZiel(e.target.value)}>{opt}</Auswahl>
                  </Feld>
                  <Btn variante="voll" disabled={busy || !zfQuelle || !zfZiel || zfQuelle === zfZiel}
                    onClick={katalogZusammenfuehren}>{t("Im Katalog zusammenführen")}</Btn>
                </>
              );
            })()}
          </>
        )}
      </section>

      <section className="wl-adminblock">
        <h3 className="wl-adminTitel">Konten · {daten.nutzer.length}</h3>
        {daten.nutzer.length === 0 ? (
          <Leer text={t("Keine Konten vorhanden.")} />
        ) : (
          <div className="wl-scroll">
            <table className="wl-tabelle wl-tabelle--kompakt">
              <thead>
                <tr><th>{t("Benutzername")}</th><th>{t("Registriert")}</th><th className="num">{t("Stand")}</th><th>{t("Zuletzt")}</th><th>Teilen &amp; Weinkarten</th><th>{t("Wetter")}</th><th>{t("Katalog")}</th><th className="wl-rechts"> </th></tr>
              </thead>
              <tbody>
                {daten.nutzer.map((n) => (
                  <tr key={n.id}>
                    <td>
                      {n.benutzername}
                      {n.istAdmin && <span className="wl-adminmarke">{t("Admin")}</span>}
                      {n.istIch && <span className="wl-nebenzeile">das bist du</span>}
                    </td>
                    <td>{datum(n.erstellt)}</td>
                    <td className="num">{n.version}</td>
                    <td>{datum(n.aktualisiert)}</td>
                    <td>
                      <label className="wl-proschalter" title={t("Keller teilen und Weinkarten für dieses Konto freischalten")}>
                        <input type="checkbox" disabled={busy || n.istAdmin}
                          checked={n.istAdmin || !!n.darfWeinkarten}
                          onChange={(e) => setzeMerkmal(n, "weinkarten", e.target.checked)} />
                        <span>{n.istAdmin ? "immer" : (n.darfWeinkarten ? "frei" : "gesperrt")}</span>
                      </label>
                    </td>
                    <td>
                      <label className="wl-proschalter" title={t("Wetterdaten für dieses Konto freischalten")}>
                        <input type="checkbox" disabled={busy || n.istAdmin}
                          checked={n.istAdmin || !!n.darfWetter}
                          onChange={(e) => setzeMerkmal(n, "wetter", e.target.checked)} />
                        <span>{n.istAdmin ? "immer" : (n.darfWetter ? "frei" : "gesperrt")}</span>
                      </label>
                    </td>
                    <td>
                      <label className="wl-proschalter"
                        title={t("Nimmt dieses Konto am gemeinsamen Katalog teil? Abschalten stoppt Zulieferungen und Aktualisierungen.")}>
                        <input type="checkbox" disabled={busy || n.istAdmin}
                          checked={n.istAdmin || n.darfKatalog !== false}
                          onChange={(e) => setzeMerkmal(n, "katalog", e.target.checked)} />
                        <span>{n.istAdmin ? "immer" : (n.darfKatalog !== false ? "teilnehmend" : "ausgeschlossen")}</span>
                      </label>
                    </td>
                    <td className="wl-rechts">
                      <LoeschenKnopf
                        label={t("Konto löschen")}
                        disabledGrund={n.istIch ? "Das eigene Konto kann hier nicht gelöscht werden." : null}
                        onDelete={() => loescheNutzer(n)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="wl-hinweis">
          Beim Löschen eines Kontos werden dessen Kellerbuch, Sitzungen und Etikettenfotos
          unwiderruflich mit entfernt.
        </p>
      </section>

      {fehler && <p className="wl-fehler">{fehler}</p>}
      <div className="wl-modalfuss"><div className="wl-modalfuss-rechts">
        <Btn variante="voll" onClick={onClose}>{t("Schließen")}</Btn>
      </div></div>
    </>
  );
}


export { AdminPanel, ImportBericht, KellerLoeschenForm, WetterLadenPanel };
